/**
 * `GET /tenants/{t}/funded-window`.
 *
 * A failure here is never fatal to a screen: the status line renders from
 * the balance alone and the missed-schedule/shed-accounts blocks render
 * nothing at all. `retry: false` is deliberate for the same reason
 * `useMe` (`src/app/session/session.ts`) sets it -- a route that has not
 * landed yet does not get closer to landing by being asked three more times
 * with a backoff delay, and every consumer of this hook treats `data`
 * undefined as "no block" whether that undefined came from a 404 or from the
 * query still loading.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { ApiFailureError } from '../../../lib/http/errors';
import { useApiClient } from '../../session/client';
import { type FundedWindow } from '../api/schemas';
import { GET_ROUTES, routePath } from '../../../lib/http/routes';

export const FUNDED_WINDOW_QUERY_KEY = (tenantId: string) =>
  ['tenant', tenantId, 'funded-window'] as const;

export function useFundedWindow(tenantId: string): UseQueryResult<FundedWindow, ApiFailureError> {
  const client = useApiClient();

  return useQuery<FundedWindow, ApiFailureError>({
    queryKey: FUNDED_WINDOW_QUERY_KEY(tenantId),
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.fundedWindow, { tenantId }),
        GET_ROUTES.fundedWindow.schema,
      ),
    retry: false,
  });
}
